import React from 'react';
import { TRAINING_TRIGGER_PHRASE } from '../../constants/training';

// Three flavours of training session, driven by what the track contains.
type TrackFlavor = 'kbOnly' | 'playbookOnly' | 'both' | 'empty';

interface TrainingInstructionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  hasPlaybook: boolean;
  kbItemCount: number;
}

const resolveFlavor = (hasPlaybook: boolean, kbItemCount: number): TrackFlavor => {
  const hasKb = kbItemCount > 0;
  if (hasPlaybook && hasKb) return 'both';
  if (hasPlaybook) return 'playbookOnly';
  if (hasKb) return 'kbOnly';
  return 'empty';
};

interface FlavorCopy {
  title: string;
  intro: string;
  steps: React.ReactNode[];
  showTrigger: boolean;
}

/** Renders the exact trigger phrase as a highlighted inline chip. */
const TriggerChip: React.FC = () => (
  <span className="inline-block rounded-md bg-indigo-50 px-1.5 py-0.5 font-semibold text-indigo-700">
    “{TRAINING_TRIGGER_PHRASE}”
  </span>
);

const buildCopy = (flavor: TrackFlavor, kbItemCount: number): FlavorCopy => {
  switch (flavor) {
    case 'both':
      return {
        title: 'How this session works',
        intro:
          'You lead this conversation, just like a real call. The AI customer will react to what you say — it will not run the call for you.',
        steps: [
          <>Work through your <strong>sales playbook</strong> naturally — pitch, qualify, and handle the customer’s reactions.</>,
          <>When you’re ready to hand over, ask the customer <TriggerChip />.</>,
          <>That’s the cue — the AI customer will then raise its questions and objections one at a time for you to handle.</>,
          <>Once you’ve worked through them, the customer will move toward discussing next steps.</>,
        ],
        showTrigger: true,
      };
    case 'playbookOnly':
      return {
        title: 'How this session works',
        intro:
          'You lead this conversation, just like a real call. The AI customer will react to what you say — it will not run the call for you.',
        steps: [
          <>Work through your <strong>sales playbook</strong> naturally — pitch, qualify, and progress the deal.</>,
          <>The AI customer responds as a realistic buyer: asking clarifying questions, raising concerns, and pushing back on weak answers.</>,
          <>As you cover the playbook, the customer will move toward discussing next steps.</>,
        ],
        showTrigger: false,
      };
    case 'kbOnly':
      return {
        title: 'How this session works',
        intro:
          'You lead this conversation, just like a real call. The AI customer will react to what you say — it will not run the call for you.',
        steps: [
          <>Open the call and pitch the way you normally would.</>,
          <>When you’re ready, invite the customer to raise questions by asking <TriggerChip /></>,
          <>That’s the cue — the AI customer will then bring up its {kbItemCount > 0 ? `${kbItemCount} ` : ''}questions and objections one at a time for you to handle.</>,
        ],
        showTrigger: true,
      };
    default:
      return {
        title: 'How this session works',
        intro:
          'You lead this conversation. Speak naturally and the AI customer will respond. End the session when you’re done.',
        steps: [],
        showTrigger: false,
      };
  }
};

/**
 * Pre-session instructional modal. Explains the rep-driven flow and (when
 * relevant) the trigger phrase that hands off to the AI customer’s questions.
 * Copy adapts to whether the track has a playbook, KB items, or both.
 */
const TrainingInstructionsModal: React.FC<TrainingInstructionsModalProps> = ({
  isOpen,
  onClose,
  hasPlaybook,
  kbItemCount,
}) => {
  if (!isOpen) return null;

  const flavor = resolveFlavor(hasPlaybook, kbItemCount);
  const copy = buildCopy(flavor, kbItemCount);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="training-instructions-title"
    >
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-start gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100">
            <svg className="h-6 w-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 id="training-instructions-title" className="mt-1 text-xl font-bold text-gray-900">
            {copy.title}
          </h3>
        </div>

        <p className="mb-4 text-sm leading-relaxed text-gray-600">{copy.intro}</p>

        {copy.steps.length > 0 && (
          <ol className="mb-5 space-y-3">
            {copy.steps.map((step, idx) => (
              <li key={idx} className="flex gap-3">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700">
                  {idx + 1}
                </span>
                <span className="text-sm leading-relaxed text-gray-700">{step}</span>
              </li>
            ))}
          </ol>
        )}

        {copy.showTrigger && (
          <div className="mb-5 rounded-lg border border-indigo-100 bg-indigo-50 p-3 text-sm text-indigo-900">
            <span className="font-semibold">Remember the cue: </span>
            ask <TriggerChip /> when you want the customer to start asking questions. You don’t have to say it word-for-word.
          </div>
        )}

        <button
          type="button"
          onClick={onClose}
          className="w-full rounded-lg bg-indigo-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-indigo-700"
        >
          Got it — let’s start
        </button>
      </div>
    </div>
  );
};

export default TrainingInstructionsModal;
